import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BRAND, INK, LINE, SURFACE, MUTED, chip, btn, btnSm, iconBtn, text, SHADOW_MODAL, SHADOW_POP, WHITE, ROW_LINE, NEUTRAL_BG, DANGER, HOVER_BG, FAINT, TEXT, SCRIM } from "./constants";
import { X, ChevronLeft, ChevronDown, CircleHelp, MoreHorizontal, Check } from "lucide-react";
import { useIsMobile } from "./lib/responsive";

// ─── CERRAR, VOLVER Y CONFIRMAR (15/10) ──────────────────────────────────────
// Una sola versión de cada una para toda la web. Antes había siete botones de
// cerrar distintos ("✕" suelto, "×", cuadros grises, círculos…).
export const CloseButton = ({ onClick, label = "Close", style }) => {
  const isMobile = useIsMobile();
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      style={{ ...iconBtn, ...(isMobile ? { width: 40, height: 40 } : null), ...style }}>
      <X size={20} strokeWidth={1.75} />
    </button>
  );
};
// Volver: solo la flecha gris (40×40 en el móvil). Con label, la flecha y el texto en gris.
// "?" en un círculo junto al título: abre el "Cómo funciona" de esa pantalla.
// GEMELO de HelpButton en components/Layout.jsx del portal.
export const HelpButton = ({ onClick, label = "How it works" }) => {
  const isMobile = useIsMobile();
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      style={{ width: isMobile ? 40 : 32, height: isMobile ? 40 : 32, border: "none", background: "transparent", color: MUTED, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, borderRadius: 999, flexShrink: 0 }}
      onMouseEnter={e => { e.currentTarget.style.color = BRAND; }} onMouseLeave={e => { e.currentTarget.style.color = MUTED; }}>
      <CircleHelp size={20} strokeWidth={1.75} />
    </button>
  );
};

export const BackButton = ({ onClick, label, ariaLabel = "Back", style }) => {
  const isMobile = useIsMobile();
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel}
      style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, display: "inline-flex", alignItems: "center", gap: 4, padding: 0, fontFamily: "inherit", fontSize: 13, fontWeight: 600, flexShrink: 0, ...(isMobile && !label ? { width: 40, height: 40, justifyContent: "center", marginLeft: -10 } : null), ...(isMobile && label ? { minHeight: 40 } : null), ...style }}>
      <ChevronLeft size={18} strokeWidth={1.75} />{label}
    </button>
  );
};
// Confirmación en la página: contenedor neutro, color SOLO en el botón que
// ejecuta, cancelar siempre gris. Gemelo de ConfirmBar del portal.
// Desde el 23/10, las de botón rojo (borrar, quitar, rechazar, descartar) salen
// SIEMPRE en ventana flotante (ConfirmDialog): una barra en la página pasaba
// desapercibida. Las demás (enviar, guardar, archivar) siguen en la página.
export const ConfirmBar = ({ message, actionLabel, cancelLabel, onConfirm, onCancel, danger = true, busy = false, style, children }) => {
  if (danger) return <ConfirmDialog message={message} actionLabel={actionLabel} cancelLabel={cancelLabel} onConfirm={onConfirm} onCancel={onCancel} busy={busy}>{children}</ConfirmDialog>;
  return <InlineConfirm message={message} actionLabel={actionLabel} cancelLabel={cancelLabel} onConfirm={onConfirm} onCancel={onCancel} busy={busy} style={style}>{children}</InlineConfirm>;
};
const InlineConfirm = ({ message, actionLabel, cancelLabel, onConfirm, onCancel, danger = false, busy = false, style, children }) => {
  const isMobile = useIsMobile();
  const size = isMobile ? { flex: "1 1 0", minHeight: 44 } : btnSm;
  return (
    <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: isMobile ? 12 : "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", ...style }}>
      <span style={{ fontSize: 12, lineHeight: 1.6, color: INK, flex: isMobile ? "1 1 100%" : "1 1 200px" }}>{message}</span>
      {children}
      <button type="button" onClick={onConfirm} disabled={busy} style={{ ...(danger ? btn.danger : btn.primary), ...size, opacity: busy ? 0.6 : 1 }}>{actionLabel}</button>
      <button type="button" onClick={onCancel} style={{ ...btn.ghost, ...size }}>{cancelLabel}</button>
    </div>
  );
};

// Confirmación flotante (23/10): la misma pregunta que ConfirmBar, pero en una
// ventana centrada sobre la pantalla con el fondo oscurecido. Para borrar algo
// desde una tabla larga, donde una barra arriba pasa desapercibida. Mismas
// reglas: color SOLO en el botón que ejecuta, cancelar gris. Escape o un clic
// fuera cancelan; el foco va a Cancelar para que Enter no borre por descuido.
export const ConfirmDialog = ({ title, message, actionLabel, cancelLabel, onConfirm, onCancel, danger = true, busy = false, children }) => {
  const isMobile = useIsMobile();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);
  const size = isMobile ? { flex: "1 1 0", minHeight: 44 } : btnSm;
  // Directo en <body>: dentro de una tarjeta con transform (Tablero, modales
  // animados) un position:fixed se queda encerrado en la tarjeta
  return createPortal(
    <div onClick={e => { e.stopPropagation(); onCancel(); }} style={{ position: "fixed", inset: 0, zIndex: 3500, background: SCRIM, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="alertdialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}
        style={{ background: WHITE, borderRadius: 14, boxShadow: SHADOW_MODAL, padding: isMobile ? 18 : 22, width: "100%", maxWidth: 440, boxSizing: "border-box" }}>
        {title && <div style={{ ...text.cardTitle, marginBottom: 8 }}>{title}</div>}
        <div style={{ ...text.body, color: INK, lineHeight: 1.6 }}>{message}</div>
        {children && <div style={{ marginTop: 12 }}>{children}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button type="button" autoFocus onClick={onCancel} style={{ ...btn.ghost, ...size }}>{cancelLabel}</button>
          <button type="button" onClick={onConfirm} disabled={busy} style={{ ...(danger ? btn.danger : btn.primary), ...size, opacity: busy ? 0.6 : 1 }}>{actionLabel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── FASE DE UN CANDIDATO ─────────────────────────────────────────────────────
// Misma lógica que el Tablero: "Validado" (approved) se reparte según los datos
// (con referencias = Referencias; con evaluación = Entrevista). Fuente única de
// la Home y del embudo de Posiciones (24/10).
export const candidatePhase = (c, statuses = {}) => {
  const st = statuses[c.id] || c.status;
  if (st === "rejected") return "rejected";
  if (st === "pending") return "pending";
  if (st === "references") return "references";
  if (st === "interview") return "interview";
  if (st === "approved") {
    if (c.references >= 1 || (c.refsData || []).length > 0) return "references";
    if ((c.evalManagers || []).length > 0 && c.evalUpdatedAt) return "interview";
    return "validated";
  }
  return "pending";
};

// ─── MENÚ «⋯» Y FILA CON SELECTOR (móvil, 24/10) ─────────────────────────────
// Las dos piezas del criterio de la ficha móvil: una sola acción a la vista y
// el resto en «⋯»; los selectores como una fila que se toca y abre el selector
// nativo del teléfono. Fuente única: ficha, Home, Posiciones y Tablero.
// items: [{ label, onClick, danger }] (los falsos se ignoran).
export const MoreMenu = ({ items, label = "More actions", size = 40, width = 210 }) => {
  const [open, setOpen] = useState(false);
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  const item = { display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderTop: `1px solid ${ROW_LINE}`, padding: "0 14px", minHeight: 44, fontSize: 13, fontFamily: "inherit", color: INK, cursor: "pointer" };
  return (
    <div style={{ position: "relative", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-label={label} title={label} aria-expanded={open}
        style={{ width: size, height: size, borderRadius: 10, border: "none", background: open ? NEUTRAL_BG : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: MUTED, padding: 0 }}>
        <MoreHorizontal size={20} strokeWidth={1.75} />
      </button>
      {open && <>
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
        <div role="menu" style={{ position: "absolute", right: 0, top: size + 4, zIndex: 21, background: WHITE, border: "1.5px solid " + LINE, borderRadius: 12, boxShadow: SHADOW_POP, width, overflow: "hidden" }}>
          {list.map((it, i) => (
            <button key={i} type="button" role="menuitem" onClick={() => { setOpen(false); it.onClick(); }}
              style={{ ...item, ...(i === 0 ? { borderTop: "none" } : null), ...(it.danger ? { color: DANGER } : null) }}>{it.label}</button>
          ))}
        </div>
      </>}
    </div>
  );
};

// Fila «Etiqueta · valor ›». Con options ([[valor, texto]]) y onChange, un
// <select> invisible encima la hace pulsable; sin ellas es solo lectura.
// cell: la misma pieza como celda de una franja (etiqueta arriba, valor debajo),
// para las cabeceras de escritorio.
// Lista de opciones de un desplegable (escritorio), con el formato del menú ⋯.
// La usan SelectRow y el selector «vs» de Comparar (01/11): una sola lista,
// nunca la nativa de Windows. `align`: lado del que cuelga.
export const OptionList = ({ options, value, onPick, onClose, align = "right", offset = 8 }) => (<>
  <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
  <div role="listbox" style={{ position: "absolute", top: "calc(100% - 4px)", [align]: offset, zIndex: 21, background: WHITE, border: "1.5px solid " + LINE, borderRadius: 12, boxShadow: SHADOW_POP, minWidth: 200, maxHeight: 320, overflowY: "auto", padding: "4px 0" }}>
    {options.map(([v, l]) => {
      const on = String(v) === String(value);
      return (
        <button key={v} type="button" role="option" aria-selected={on} onClick={() => { onClose(); if (!on) onPick(v); }}
          onMouseEnter={e => { e.currentTarget.style.background = HOVER_BG; }} onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", background: "none", border: "none", padding: "0 14px", minHeight: 38, fontSize: 13, fontWeight: on ? 700 : 500, fontFamily: "inherit", color: on ? BRAND : INK, cursor: "pointer", whiteSpace: "nowrap" }}>
          {l}{on && <Check size={15} strokeWidth={2} color={BRAND} />}
        </button>
      );
    })}
  </div>
</>);

// Embudo en flechas grises (02/11): la ÚNICA versión, la de Posiciones; la
// Home usa la misma. `stages`: [{ key, label, n, node? }] (node sustituye a
// la cifra, p. ej. el campo editable de Interesados). Debajo de cada cifra, el
// % que pasa de la anterior.
// compact (Home, 03/11): cifra a 15 y solo el %, para que pese lo mismo que
// las filas de al lado; Posiciones lo usa a tamaño completo
export const FunnelArrows = ({ stages, lang, compact = false }) => {
  const ofPrev = (i) => {
    if (!i) return null;
    const prev = stages[i - 1];
    if (!prev.n) return "–";
    const pct = `${Math.round((stages[i].n / prev.n) * 100)}%`;
    return compact ? pct : `${pct} ${lang === "es" ? "de" : "of"} ${String(prev.label).toLowerCase()}`;
  };
  const padY = compact ? 8 : 12;
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {stages.map((f, i) => {
        const first = i === 0, last = i === stages.length - 1;
        // Bloques en flecha: el primero sin muesca, el último sin punta
        const shape = first ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)"
          : last ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)"
          : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)";
        return (
          <div key={f.key} style={{ flex: "1 1 0", minWidth: 0, background: NEUTRAL_BG, clipPath: shape, borderRadius: first ? "10px 0 0 10px" : last ? "0 10px 10px 0" : 0,
            padding: first ? `${padY}px 22px ${padY}px 10px` : last ? `${padY}px 10px ${padY}px 22px` : `${padY}px 18px`, textAlign: "center" }}>
            <div style={{ ...text.label, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.label}</div>
            {f.node || <div style={{ fontSize: compact ? 15 : 22, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{f.n}</div>}
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2, minHeight: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ofPrev(i)}</div>
          </div>
        );
      })}
    </div>
  );
};

export const SelectRow = ({ label, display, value, options, onChange, ariaLabel, first = false, cell = false }) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const editable = !!(options && onChange);
  // Móvil: el selector nativo del teléfono (es el que mejor se usa con el
  // dedo). Escritorio: una lista propia con el formato del menú ⋯, porque la
  // nativa de Windows salía con letra y bordes del sistema (24/10).
  const picker = !editable ? null : isMobile ? (
    <select aria-label={ariaLabel || label} value={value} onChange={e => onChange(e.target.value)}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, fontSize: 16, cursor: "pointer" }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  ) : (<>
    <button type="button" aria-label={ariaLabel || label} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "transparent", border: "none", cursor: "pointer", padding: 0 }} />
    {open && <OptionList options={options} value={value} onPick={onChange} onClose={() => setOpen(false)} align={cell ? "left" : "right"} offset={cell ? 12 : 8} />}
  </>);
  if (cell) return (
    <div style={{ position: "relative", flex: "1 1 0", minWidth: 0, padding: "12px 20px", borderLeft: first ? "none" : `1px solid ${ROW_LINE}` }}>
      <div style={{ ...text.label, fontSize: 11 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 700, color: editable ? BRAND : INK, marginTop: 4 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</span>
        {editable && <ChevronDown size={16} strokeWidth={1.75} color={MUTED} style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />}
      </div>
      {picker}
    </div>
  );
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 48, padding: "0 16px", borderTop: first ? "none" : `1px solid ${ROW_LINE}` }}>
      <span style={{ ...text.meta, fontSize: 12, flexShrink: 0 }}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 700, color: editable ? BRAND : INK, minWidth: 0, textAlign: "right" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</span>
        {editable && <ChevronDown size={16} strokeWidth={1.75} color={MUTED} style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />}
      </span>
      {picker}
    </div>
  );
};

// ─── CRITERIOS DE EVALUACIÓN ──────────────────────────────────────────────────
// Las preguntas con las que el equipo del cliente puntúa cada entrevista
// (escala 1-3). Fuente única: la pestaña Evaluate de la ficha, la pestaña
// Evaluación de la posición, la tabla y el Wall del pipeline leen de aquí.
//
// Cada posición puede tener sus propias preguntas (positions.eval_questions):
//   { sections: { c1: "Fit técnico", … }, items: [{ id: "c1a", label: "…" }, …] }
// Sin eso, se usan las 8 por defecto. Las secciones son SIEMPRE los 4 vectores
// de la metodología (c1 técnico, c2 soft, c3 cultural, c4 potencial): se
// puede cambiar su nombre, no cuántos son. El vector de cada pregunta va en
// el prefijo de su id (`c3…` = cultural), y por eso el radar y las medias por
// vector siguen funcionando con preguntas nuevas.
export const EVAL_SECTIONS = ["c1", "c2", "c3", "c4"];
export const EVAL_VECTOR = { c1: "technical", c2: "soft", c3: "cultural", c4: "growth" };
const EVAL_LETTER = { c1: "T", c2: "S", c3: "C", c4: "G" };

export const defaultEvalSections = (lang) => {
  const es = lang === "es";
  return { c1: es ? "Fit técnico" : "Technical fit", c2: es ? "Habilidades blandas" : "Soft skills", c3: es ? "Fit cultural" : "Cultural fit", c4: es ? "Potencial" : "Potential/growth" };
};

export const defaultEvalItems = (lang) => {
  const es = lang === "es";
  // Textos en inglés reescritos el 31/10 (sonaban a traducción); mismo sentido, mismos ids
  return [
    { id: "c1a", label: es ? "Me da confianza en su capacidad" : "Confident they can do the job" },
    { id: "c1b", label: es ? "Ha hecho esto antes" : "Has done this before" },
    { id: "c2a", label: es ? "Comunicación" : "Communication" },
    { id: "c2b", label: es ? "Justifica bien sus razones" : "Explains their reasoning well" },
    { id: "c3a", label: es ? "Me lo imagino en el equipo" : "I can picture them on the team" },
    { id: "c3b", label: es ? "Acostumbrado a entornos rápidos" : "Used to a fast pace" },
    { id: "c4a", label: es ? "Capacidad de adaptación" : "Adapts to change" },
    { id: "c4b", label: es ? "Ha crecido en responsabilidades" : "Has grown in responsibility" },
  ];
};

// Devuelve las secciones con sus preguntas: [{ id: "c1", name, category: "1. …",
// items: [{ id, short: "T1", label }] }]. Las secciones sin preguntas se omiten.
export const evalCriteria = (lang, config = null) => {
  const names = { ...defaultEvalSections(lang) };
  EVAL_SECTIONS.forEach(s => { const v = config?.sections?.[s]; if (typeof v === "string" && v.trim()) names[s] = v.trim(); });
  const items = Array.isArray(config?.items) && config.items.length ? config.items : defaultEvalItems(lang);
  return EVAL_SECTIONS.map((s, i) => ({
    id: s, name: names[s], category: `${i + 1}. ${names[s]}`,
    items: items.filter(it => String(it.id).slice(0, 2) === s).map((it, k) => ({ id: it.id, short: `${EVAL_LETTER[s]}${k + 1}`, label: it.label })),
  })).filter(sec => sec.items.length > 0);
};

export const evalItemIds = (lang, config = null) => evalCriteria(lang, config).flatMap(sec => sec.items.map(it => it.id));

// % de una entrevista sobre las preguntas PUNTUADAS (una pregunta sin puntuar
// no cuenta como 0). null si no hay ninguna puntuada.
export const evalScorePct = (s, ids) => {
  const vals = ids.map(k => (s || {})[k]).filter(v => v > 0);
  return vals.length ? (vals.reduce((a, b) => a + b, 0) / (vals.length * 3)) * 100 : null;
};

// Media /5 de varias entrevistas (una por entrevistador). null si no hay datos.
export const evalAvg5 = (managers, scores, ids) => {
  const ps = (managers || []).map(m => evalScorePct((scores || {})[m.id], ids)).filter(p => p !== null);
  return ps.length ? parseFloat((ps.reduce((a, b) => a + b, 0) / ps.length / 20).toFixed(1)) : null;
};

// Media /5 por vector ({ technical, soft, cultural, growth }); 0 si un vector
// no tiene puntuaciones.
export const evalByVector = (managers, scores, ids) => {
  const out = {};
  EVAL_SECTIONS.forEach(s => { out[EVAL_VECTOR[s]] = evalAvg5(managers, scores, ids.filter(i => String(i).slice(0, 2) === s)) ?? 0; });
  return out;
};

// ─── CANDIDATE AVATAR ─────────────────────────────────────────────────────────
export const CandidateAvatar = ({ name, size = 32 }) => {
  const tones = [
    { bg: LINE, face: FAINT, hair: TEXT, shirt: MUTED },
    { bg: LINE, face: FAINT, hair: TEXT, shirt: MUTED },
    { bg: LINE, face: FAINT, hair: TEXT, shirt: MUTED },
    { bg: LINE, face: FAINT, hair: INK, shirt: MUTED },
    { bg: LINE, face: FAINT, hair: TEXT, shirt: MUTED },
  ];
  const t = tones[name.charCodeAt(0) % tones.length];
  const isFemale = name.split(" ").some(w => ["elena","beatriz","sofia","maria","laura","lucia","marta"].includes(w.toLowerCase()));
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ borderRadius: "50%", flexShrink: 0, display: "block", border: `1.5px solid ${LINE}` }}>
      <circle cx="50" cy="50" r="50" fill={t.bg} />
      <ellipse cx="50" cy="105" rx="40" ry="30" fill={t.shirt} />
      <path d="M42 78 L50 85 L58 78" fill={FAINT} />
      <rect x="44" y="65" width="12" height="14" rx="5" fill={t.face} />
      <ellipse cx="50" cy="50" rx="19" ry="22" fill={t.face} />
      {isFemale ? (
        <>
          <ellipse cx="50" cy="31" rx="19" ry="9" fill={t.hair} />
          <ellipse cx="31" cy="48" rx="5" ry="16" fill={t.hair} />
          <ellipse cx="69" cy="48" rx="5" ry="16" fill={t.hair} />
          <ellipse cx="50" cy="28" rx="14" ry="6" fill={t.hair} />
        </>
      ) : (
        <>
          <ellipse cx="50" cy="31" rx="19" ry="10" fill={t.hair} />
          <ellipse cx="50" cy="28" rx="15" ry="7" fill={t.hair} />
        </>
      )}
      <ellipse cx="31" cy="50" rx="3" ry="4" fill={t.face} />
      <ellipse cx="69" cy="50" rx="3" ry="4" fill={t.face} />
      <ellipse cx="43" cy="50" rx="3" ry="2.5" fill={TEXT} />
      <ellipse cx="57" cy="50" rx="3" ry="2.5" fill={TEXT} />
      <circle cx="44.5" cy="49" r="1" fill={WHITE} opacity="0.5" />
      <circle cx="58.5" cy="49" r="1" fill={WHITE} opacity="0.5" />
      <path d="M48 55 Q50 59 52 55" stroke={MUTED} strokeWidth="1" fill="none" strokeLinecap="round" />
      <path d="M44 62 Q50 66 56 62" stroke={MUTED} strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  );
};

// ─── ICON ─────────────────────────────────────────────────────────────────────
export const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    bell: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    archive: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    lifeline: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 0 0 4.93 19.07M19.07 19.07A10 10 0 0 0 4.93 4.93"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    file: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    chevronRight: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
    chevronLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    starEmpty: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    moreH: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>,
    radar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 6a6 6 0 1 0 0 12A6 6 0 0 0 12 6z"/><path d="M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/><line x1="22" y1="12" x2="14" y2="12"/></svg>,
    trendUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  };
  return icons[name] || null;
};

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
export const StatusBadge = ({ status, t, hasRefs, hasEval }) => {
  let displayStatus = status;
  if (status === "approved") {
    if (hasRefs) displayStatus = "references";
    else if (hasEval) displayStatus = "interview";
    else displayStatus = "validated";
  }
  if (status === "interview") displayStatus = "interview";
  if (status === "references") displayStatus = "references";
  if (status === "rejected") displayStatus = "rejected";
  const cfg = {
    pending:    { label: t.pending },
    validated:  { label: t.validated === "Validados" ? "Validado" : "Validated" },
    interview:  { label: t.validated === "Validados" ? "Entrevista" : "Interview" },
    references: { label: t.references },
    rejected:   { label: t.rejected },
  };
  const c = cfg[displayStatus] || cfg.pending;
  return (
    <span style={{ ...chip(false), whiteSpace: "nowrap" }}>{c.label}</span>
  );
};

// ─── SCORE BAR ────────────────────────────────────────────────────────────────
export const ScoreBar = ({ label, value, max = 5, color = BRAND }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
      <span style={{ fontSize: 13, color: TEXT }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}/{max}</span>
    </div>
    <div style={{ background: HOVER_BG, borderRadius: 4, height: 6 }}>
      <div style={{ background: color, borderRadius: 4, height: 6, width: `${(value / max) * 100}%`, transition: "width 0.6s ease" }} />
    </div>
  </div>
);

// ─── METRIC PILL ──────────────────────────────────────────────────────────────
// Cifra en negro: el azul es solo para lo que se puede pulsar
export const MetricPill = ({ label, value, total, color = INK }) => (
  // Alto fijo: los boxes de la fila de métricas deben medir todos lo mismo
  // aunque su contenido varíe.
  <div style={{ height: 62, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 14px", background: SURFACE, borderRadius: 10, border: `1.5px solid ${LINE}`, minWidth: 70 }}>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{total && <span style={{ color: FAINT }}>/{total} </span>}{label}</div>
  </div>
);

// ─── JD FORMATTED ────────────────────────────────────────────────────────────
// Pinta texto markdown-ish (JD, culture doc) como documento: títulos de sección
// en BRAND mayúsculas con una línea encima, viñetas con punto, listas
// numeradas y **negritas** dentro de cualquier línea. Fuente única para todas
// las pantallas que enseñan un documento de la posición o del cliente.
const renderInline = (txt) => {
  const parts = txt.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, j) => {
    const m = p.match(/^\*\*(.+)\*\*$/);
    return m ? <strong key={j} style={{ color: INK, fontWeight: 700 }}>{m[1]}</strong> : p;
  });
};

// Línea separadora de markdown (---, ***, ___). La IA a veces la mete entre
// secciones del documento de cultura (31/10) y se pintaba tal cual: aquí se
// ignora (cada título ya lleva su propia línea) y al generar se quita.
export const isRuleLine = (s) => /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(s || "");
export const stripRules = (txt) => (txt || "").replace(/\r/g, "").split("\n").filter(l => !isRuleLine(l)).join("\n").replace(/\n{3,}/g, "\n\n").trim();

export const JDFormatted = ({ text }) => {
  if (!text) return null;
  const lines = text.replace(/\r/g, "").split("\n").filter(l => !isRuleLine(l));
  const elements = [];
  let sections = 0;
  const heading = (key, label) => {
    elements.push(
      <div key={key} style={{ ...text.label,
        marginTop: sections ? 22 : 4, marginBottom: 10, paddingTop: sections ? 18 : 0, borderTop: sections ? `1px solid ${LINE}` : "none" }}>
        {label}
      </div>
    );
    sections++;
  };
  const bullet = (key, marker, txt) => elements.push(
    <div key={key} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6, paddingLeft: 2 }}>
      {marker
        ? <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, minWidth: 16, lineHeight: 1.7 }}>{marker}</span>
        : <div style={{ width: 5, height: 5, borderRadius: "50%", background: FAINT, marginTop: 8, flexShrink: 0 }} />}
      <span style={{ fontSize: 13, color: INK, lineHeight: 1.7 }}>{renderInline(txt)}</span>
    </div>
  );
  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) { elements.push(<div key={i} style={{ height: 6 }} />); return; }
    // Títulos explícitos: **TÍTULO** en línea propia o # / ## / ### Título
    const headingMatch = trimmed.match(/^\*\*([^*]+?)\*\*:?$/) || trimmed.match(/^#{1,4}\s+(.+)$/);
    if (headingMatch) { heading(i, headingMatch[1].replace(/\*\*/g, "")); return; }
    // Título implícito: línea corta toda en MAYÚSCULAS ("ROLE OVERVIEW")
    const hasLetters = /[A-Za-zÁÉÍÓÚÑáéíóúñ]/.test(trimmed);
    const isAllUpper = trimmed === trimmed.toUpperCase();
    const isListLine = /^([•\-*]|\d+[.)])\s/.test(trimmed);
    if (hasLetters && isAllUpper && trimmed.length <= 60 && !/[.?!:,]$/.test(trimmed) && !isListLine) { heading(i, trimmed); return; }
    // Viñetas: •, -, *
    const b = trimmed.match(/^[•\-*]\s+(.+)$/);
    if (b) { bullet(i, null, b[1]); return; }
    // Lista numerada: 1. / 1)
    const n = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
    if (n) { bullet(i, n[1] + ".", n[2]); return; }
    // Párrafo normal, con las negritas que traiga
    elements.push(<p key={i} style={{ fontSize: 13, color: INK, lineHeight: 1.75, margin: "0 0 8px" }}>{renderInline(trimmed)}</p>);
  });
  return <div>{elements}</div>;
};

// ─── REF STATUS BADGE ────────────────────────────────────────────────────────
// Mismo chip gris que el resto de estados: el estado lo dice el texto, no el color
export const RefStatusBadge = ({ status }) => (
  <span style={{ ...chip(false), whiteSpace: "nowrap" }}>{status}</span>
);

// ─── BLANK QUAL SECTIONS ─────────────────────────────────────────────────────
export const BLANK_QUAL_SECTIONS = [
  { section: "Personal data", items: [
    { n: "1.1", q: "Name", a: "" }, { n: "1.2", q: "City", a: "" },
    { n: "1.3", q: "Current salary / expectation", a: "" }, { n: "1.4", q: "Notice period", a: "" },
  ]},
  { section: "Technical fit", items: [
    { n: "2.1", q: "Do you have experience in inorganic growth (M&A) or funding rounds?", a: "" },
    { n: "2.2", q: "Have you worked in companies in a high-growth phase, startups, or digital business models?", a: "" },
    { n: "2.3", q: "What are the key elements for successful company management?", a: "" },
    { n: "2.4", q: "What has been your greatest learning managing a company / department?", a: "" },
  ]},
  { section: "Soft skills", items: [
    { n: "3.1", q: "What are the key elements for successfully managing people?", a: "" },
    { n: "3.2", q: "Give a couple of tricks if I want people to get things done.", a: "" },
    { n: "3.3", q: "What are your greatest professional achievements?", a: "" },
    { n: "3.4", q: "What type of boss suits you best?", a: "" },
  ]},
  { section: "About you", items: [
    { n: "4.1", q: "How would the person who knows you best describe you?", a: "" },
    { n: "4.2", q: "What would your superiors, peers & subordinates say about you?", a: "" },
    { n: "4.3", q: "What do you like to do in your free time?", a: "" },
    { n: "4.4", q: "Recommend a couple of books or films.", a: "" },
  ]},
];

// ─── UUID ─────────────────────────────────────────────────────────────────────
export const genUUID = () => "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => { const r=Math.random()*16|0; return (c==="x"?r:((r & 0x3) | 0x8)).toString(16); });

// ── Ventanita al pasar el ratón (31/10): GEMELA de HoverCard/placeFloat de
// Layout.jsx del portal, misma regla. Anclada a la pieza (no al ratón), a su
// DERECHA y alineada con su borde de arriba; si no cabe, a la izquierda; si
// tampoco, debajo. Nunca fuera de la ventana. Va directa a <body>. `anchor` es
// una función que devuelve el elemento (se lee después de pintar).
export function placeFloat(rect, w, h, gap = 10) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = 12;
  let left = rect.right + gap;
  let top = rect.top;
  if (left + w > vw - m) {
    left = rect.left - gap - w;
    if (left < m) { left = Math.min(Math.max(m, rect.left), vw - w - m); top = rect.bottom + gap; }
  }
  top = Math.min(Math.max(m, top), Math.max(m, vh - h - m));
  return { left, top };
}

export function HoverCard({ anchor, open, width = 320, onMouseEnter, onMouseLeave, children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !anchor || !el) return undefined;
    const calc = () => {
      const a = anchor();
      if (!a) return;
      const { left, top } = placeFloat(a.getBoundingClientRect(), width, el.offsetHeight);
      el.style.left = `${left}px`; el.style.top = `${top}px`; el.style.visibility = "visible";
    };
    calc();
    const raf = requestAnimationFrame(calc);
    window.addEventListener("scroll", calc, true);
    window.addEventListener("resize", calc);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", calc, true); window.removeEventListener("resize", calc); };
  });
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div ref={ref} role="dialog" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      style={{ position: "fixed", left: -9999, top: 0, width, maxHeight: "min(440px, 70vh)", overflowY: "auto", overscrollBehavior: "contain", boxSizing: "border-box", zIndex: 1000, background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, boxShadow: SHADOW_POP, padding: 16, textAlign: "left", visibility: "hidden" }}>
      {children}
    </div>,
    document.body
  );
}
